import React, { useEffect } from 'react';
import AuthForm from '@/components/AuthForm';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '@/services/firebase';

const Index = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
      
      // If in an iframe, send a message to the parent window about successful login
      if (window.top !== window.self) {
        window.parent.postMessage({ type: 'auth-success', userId: user.uid }, '*');
      }
    }
  }, [user, loading, navigate]);
  
  // Listen for messages from parent iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle any messages from the parent page if needed
      console.log('Received message from parent:', event.data);
    };
    
    window.addEventListener('message', handleMessage);
    
    // Log if running in an iframe
    if (window.top !== window.self) {
      console.log("Running in an iframe");
    }
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Logo in the top left */}
      <div className="p-4">
        <img src="https://solarinphoenix.com/wp-content/uploads/2025/03/SolarInPhoenixLogoInverted.png" alt="Solar In Phoenix Logo" className="h-12" />
      </div>

      <div className="flex-grow flex flex-col md:flex-row items-center">
        <div className="w-full md:w-3/5 p-8 md:p-16 flex flex-col justify-center animate-slideDown">
          <div className="w-full mx-auto">
            <h1 className="text-5xl font-bold mb-6">
              <span className="text-gradient">Solar In Phoenix</span> Referral Program
            </h1>
            <p className="text-xl mb-8">
              Do you know homeowners or businesses interested in cutting energy costs and embracing sustainability? Partner with Solar In Phoenix's Referral Program and earn up to $3,000 per successful referral—all while supporting Arizona’s clean energy transition.
            </p>
            
            {/* How It Works Section */}
            <div className="text-xl mb-8">
              <span className="text-gradient font-bold">How It Works:</span>
              <ol className="list-decimal pl-5 space-y-2 mt-2">
                <li><b>Refer a Lead</b> - Refer homeowners or businesses exploring solar in Arizona via our referral form.</li>
                <li><b>We Handle the Rest</b> - Our expert team qualifies the lead, designs the system, and manages the installation process.</li>
                <li><b>You Get Paid</b> - Once the project is complete, you receive 50% of the profit directly into your bank account.</li>
              </ol>
            </div>

            {/* Features List */}
            <ul className="text-xl space-y-3 mb-8">
              <li>
                <span className="text-gradient font-bold">Why Join?</span>
              </li>
              {[
                { text: 'High Earnings Potential', description: 'Earn up to $3,000 per referral with no cap on total earnings.' },
                { text: 'Zero Risk or Costs', description: 'No upfront fees, contracts, or obligations.' },
                { text: 'Full Transparency', description: 'Track every referral’s status and earnings through our secure portal.' },
                { text: 'Trusted Local Installers', description: 'We collaborate with certified, top-rated solar installers across Arizona to ensure quality service for your contacts.' }
              ].map((item, index) => (
                <li key={index} className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>
                    <strong>{item.text}:</strong> {item.description}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="w-full md:w-2/5 p-8 md:p-16 flex items-center justify-center animate-slideUp">
          <AuthForm />
        </div>
      </div>
      
      {/* Footer */}
      <footer className="glass-panel bg-transparent backdrop-blur-md border-t border-white/10">
        <div className="container mx-auto px-4 py-6 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Solar In Phoenix. All rights reserved.</p>
          <a href="https://solarinphoenix.com/privacy-policy" className="text-muted-foreground hover:underline mx-2">Privacy Policy</a>
          |
          <a href="https://solarinphoenix.com/terms" className="text-muted-foreground hover:underline mx-2">Terms</a>
        </div>
      </footer>
    </div>
  );
};

export default Index;
